import { jsx as _jsx } from "react/jsx-runtime";
import { App, Form } from "antd";
import { useCallback, useRef, useState } from "react";
import { yieldMainThread } from "@devowl-wp/react-utils";
import { Prompt } from "../components/common/prompt.js";
function useFormHandler({ isEdit, defaultValues, template, entityTemplateVersion, attributes, handleSave, i18n, initialHasChanges, trackFieldsDifferFromDefaultValues = [], unloadPromptWhen }) {
    const { message } = App.useApp();
    const useDefaultValues = {
        ...defaultValues,
        ...attributes || {}
    };
    const [form] = Form.useForm();
    const [isBusy, setIsBusy] = useState(false);
    const hasChanges = useRef(initialHasChanges || false); // use ref instead of state to avoid complete form rerender
    const isTemplateUpdate = isEdit && template ? entityTemplateVersion !== template.version : false;
    const templateCheck = template ? isTemplateUpdate || !isEdit : false;
    // Do not hold in state as it could lead to input focus loss due to reactivity. Use `hasTrackedFieldDifferenceToDefaultValue` instead.
    const trackedFieldsDifferFromDefaultValues = [];
    const hasTrackedFieldDifferenceToDefaultValue = (field)=>trackedFieldsDifferFromDefaultValues.indexOf(field) > -1;
    const onFinish = useCallback(async (values)=>{
        setIsBusy(true);
        try {
            const callback = await handleSave(values);
            message.success(i18n.successMessage);
            // Wait until `initialValues` got reinitialized through antd so we do not revert
            // back to a previous form state.
            yieldMainThread().then(()=>{
                form.resetFields();
                if (typeof callback === "function") {
                    callback();
                }
            });
            hasChanges.current = false;
        } catch (e) {
            message.error(e);
        } finally{
            setIsBusy(false);
        }
    }, [
        form,
        handleSave
    ]);
    const onFinishFailed = useCallback(()=>{
        message.error(i18n.validationError);
    }, [
        form,
        i18n
    ]);
    const prompt = /*#__PURE__*/ _jsx(Prompt, {
        when: (blockerFunction)=>hasChanges.current && (unloadPromptWhen ? unloadPromptWhen(blockerFunction) : true),
        title: initialHasChanges && i18n.unloadConfirmInitialActive ? i18n.unloadConfirmInitialActive : i18n.unloadConfirm
    });
    const onValuesChange = useCallback((changedValues, values)=>{
        if (trackFieldsDifferFromDefaultValues && useDefaultValues) {
            // Change without reactivity and keep reference so `hasTrackedFieldDifferenceToDefaultValue` keeps intact
            trackedFieldsDifferFromDefaultValues.splice(0, trackedFieldsDifferFromDefaultValues.length);
            for (const trackFieldChange of trackFieldsDifferFromDefaultValues){
                const currentValue = values[trackFieldChange];
                const defaultValue = useDefaultValues[trackFieldChange];
                if (currentValue !== defaultValue) {
                    trackedFieldsDifferFromDefaultValues.push(trackFieldChange);
                }
            }
        }
        hasChanges.current = true;
    }, [
        trackFieldsDifferFromDefaultValues,
        useDefaultValues
    ]);
    return {
        defaultValues: useDefaultValues,
        template,
        isEdit,
        isTemplateUpdate,
        templateCheck,
        form,
        isBusy,
        setIsBusy,
        hasTrackedFieldDifferenceToDefaultValue,
        onFinish,
        /**
         * Cast to avoid this error:
         *
         * ```
         * The inferred type of 'useFormContentBlockerHandler' cannot be named without a reference to '.pnpm/rc-field-form@1.27.4_wcqkhtmu7mswc6yz4uyexck3ty/node_modules/rc-field-form/lib/interface'. This is likely not portable. A type annotation is necessary.
         * ```
         */ onFinishFailed: onFinishFailed,
        prompt,
        onValuesChange
    };
}
export { useFormHandler };
